import React from 'react';
import { getCategoryColor } from '../../utils/categories';

export type CategoryIconName =
  | 'Engine'
  | 'Turbo'
  | 'Drivetrain'
  | 'Fuel System'
  | 'Cooling'
  | 'Suspension'
  | 'Brakes'
  | 'Wheels'
  | 'Exhaust'
  | 'Interior'
  | 'Exterior'
  | 'Electronics'
  | 'Miscellaneous'
  | 'Overview';

type Size = 'sm' | 'md' | 'lg';

const SIZES: Record<Size, string> = {
  sm: 'w-5 h-5',
  md: 'w-7 h-7',
  lg: 'w-9 h-9',
};

/** Normalize free-text category labels to a known icon key. */
export function resolveCategoryIconName(category?: string | null): CategoryIconName {
  const raw = (category || '').trim().toLowerCase();
  if (!raw || raw === 'all' || raw === 'all systems' || raw === 'overview') return 'Overview';
  if (raw.includes('engine') || raw.includes('motor')) return 'Engine';
  if (raw.includes('turbo') || raw.includes('supercharg')) return 'Turbo';
  if (raw.includes('drive') || raw.includes('transmission') || raw.includes('clutch') || raw.includes('diff'))
    return 'Drivetrain';
  if (raw.includes('fuel')) return 'Fuel System';
  if (raw.includes('cool') || raw.includes('radiator')) return 'Cooling';
  if (raw.includes('suspension') || raw.includes('coil') || raw.includes('spring')) return 'Suspension';
  if (raw.includes('brake')) return 'Brakes';
  if (raw.includes('wheel') || raw.includes('tire') || raw.includes('tyre')) return 'Wheels';
  if (raw.includes('exhaust') || raw.includes('muffler')) return 'Exhaust';
  if (raw.includes('interior') || raw.includes('seat') || raw.includes('cabin')) return 'Interior';
  if (raw.includes('exterior') || raw.includes('body') || raw.includes('aero')) return 'Exterior';
  if (raw.includes('electron') || raw.includes('ecu') || raw.includes('wiring') || raw.includes('electric'))
    return 'Electronics';
  if (raw === 'miscellaneous' || raw === 'misc' || raw === 'other') return 'Miscellaneous';
  return 'Miscellaneous';
}

function IconPaths({ name }: { name: CategoryIconName }) {
  // 24×24 stroke icons — automotive, not emoji
  switch (name) {
    case 'Engine':
      return (
        <>
          <path d="M4 10h3l1-2h4l1 2h3v6H4v-6z" />
          <path d="M7 16v2M17 12h2v4h-2M9 8V6h2" />
        </>
      );
    case 'Turbo':
      return (
        <>
          <circle cx="12" cy="12" r="3.5" />
          <path d="M12 4.5a7.5 7.5 0 107.5 7.5" />
          <path d="M18.5 8.5c1.5 1 2.5 2.6 2.5 4.5" />
          <path d="M4 14h3" />
        </>
      );
    case 'Drivetrain':
      return (
        <>
          <circle cx="8.5" cy="12" r="3.2" />
          <circle cx="15.5" cy="12" r="3.2" />
          <path d="M11.2 12h1.6M8.5 8.8v-1.3M8.5 16.5v-1.3M15.5 8.8v-1.3M15.5 16.5v-1.3" />
        </>
      );
    case 'Fuel System':
      return (
        <>
          <path d="M6 7h8a2 2 0 012 2v9H6V7z" />
          <path d="M16 10h2.5a1.5 1.5 0 011.5 1.5V16a2 2 0 01-2 2h-.5" />
          <path d="M9 4v3M11 4v3" />
        </>
      );
    case 'Cooling':
      return (
        <>
          <path d="M12 3v18M5.5 6.5l13 11M18.5 6.5l-13 11" />
          <circle cx="12" cy="12" r="2" />
        </>
      );
    case 'Suspension':
      return (
        <>
          <path d="M6 5h12" />
          <path d="M8 5c0 2 2 2 2 4s-2 2-2 4 2 2 2 4  -2 2-2 4" />
          <path d="M14 5c0 2 2 2 2 4s-2 2-2 4 2 2 2 4-2 2-2 4" />
          <path d="M6 19h12" />
        </>
      );
    case 'Brakes':
      return (
        <>
          <circle cx="12" cy="12" r="7" />
          <circle cx="12" cy="12" r="3" />
          <path d="M5 9.5 3.5 8M19 9.5l1.5-1.5M5 14.5 3.5 16M19 14.5l1.5 1.5" />
        </>
      );
    case 'Wheels':
      return (
        <>
          <circle cx="12" cy="12" r="7.5" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M12 4.5v3M12 16.5v3M4.5 12h3M16.5 12h3M7 7l2 2M15 15l2 2M17 7l-2 2M9 15l-2 2" />
        </>
      );
    case 'Exhaust':
      return (
        <>
          <path d="M3 13h8c2 0 3-1.5 5-1.5h5" />
          <path d="M3 11h7c1.5 0 2.5 1.5 4.5 1.5" />
          <path d="M18 10.5v5M20.5 10.5v5" />
        </>
      );
    case 'Interior':
      return (
        <>
          <path d="M6 18V10l6-4 6 4v8" />
          <path d="M9 18v-5h6v5" />
          <path d="M10 10.5h4" />
        </>
      );
    case 'Exterior':
      return (
        <>
          <path d="M3 14h18l-1.5-4.5a2 2 0 00-1.9-1.3H7.4a2 2 0 00-1.9 1.3L4 14z" />
          <path d="M5 14v3h2.5v-1.5h9V17H19v-3" />
          <circle cx="7.5" cy="17.5" r="1.2" />
          <circle cx="16.5" cy="17.5" r="1.2" />
        </>
      );
    case 'Electronics':
      return (
        <>
          <rect x="7" y="7" width="10" height="10" rx="1.5" />
          <path d="M10 10h4v4h-4zM12 3v3M12 18v3M3 12h3M18 12h3M5.5 5.5l2 2M16.5 16.5l2 2M18.5 5.5l-2 2M7.5 16.5l-2 2" />
        </>
      );
    case 'Overview':
      return (
        <>
          <path d="M4 16l8-10 8 10" />
          <path d="M7 16h10v3H7z" />
          <path d="M9 11.5h6" />
        </>
      );
    case 'Miscellaneous':
    default:
      return (
        <>
          <path d="M14.7 6.3a4 4 0 01-1.4 5.5l-6 3.5-2-2 3.5-6a4 4 0 015.9-.999z" />
          <path d="M5.5 16.5 4 20l3.5-1.5" />
          <path d="M16 4l4 4" />
        </>
      );
  }
}

export const CategoryIcon: React.FC<{
  category?: string | null;
  size?: Size;
  color?: string;
  className?: string;
  title?: string;
}> = ({ category, size = 'md', color, className = '', title }) => {
  const name = resolveCategoryIconName(category);
  const stroke = color || getCategoryColor(name === 'Overview' ? 'Miscellaneous' : name);
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${SIZES[size]} ${className}`}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      <IconPaths name={name} />
    </svg>
  );
};

export default CategoryIcon;
